import { isAxiosError } from 'axios';
import type { PaginationMeta } from '@gokrew/models/common';
import {
  BillingErrorCode,
  BillingInvoiceStatus,
  BillingPlanState,
  type BillingInvoicePage,
  type BillingPaymentMethod,
  type BillingSeatLimitDetails,
} from '@gokrew/models/billing';
import { Tone } from '@gokrew/models/ui.types';
import { CARD_BRAND_NAMES } from '@/constants/billing';

/** Billing is Canadian: format in the Canadian variant of the language. */
export const billingLocale = (language: string) =>
  language.startsWith('fr') ? 'fr-CA' : 'en-CA';

/** `4,95 $` / `$4.95`, falling back to `4.95 CAD` for an unknown currency. */
export const formatMoney = (
  amount: number,
  currency: string,
  language: string
): string => {
  try {
    return new Intl.NumberFormat(billingLocale(language), {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * A `YYYY-MM-DD` calendar day ("Oct 1, 2026" / "1 oct. 2026"), formatted
 * in UTC so it never moves a day in a time zone west of Greenwich.
 */
export const formatBillingDate = (day: string, language: string): string => {
  const match = CALENDAR_DAY.exec(day);
  if (!match) return day;
  const [, year, month, date] = match;
  return new Intl.DateTimeFormat(billingLocale(language), {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(+year, +month - 1, +date)));
};

/**
 * The 402 the paywall answers when adding an active employee would take the
 * company past its free tier, or null for any other error.
 */
export const getSeatLimitDetails = (
  error: unknown
): Partial<BillingSeatLimitDetails> | null => {
  if (!isAxiosError(error) || error.response?.status !== 402) return null;
  const data = error.response.data as
    { errorCode?: unknown; errorDetails?: unknown } | undefined;
  if (data?.errorCode !== BillingErrorCode.SeatLimitReached) return null;
  return data.errorDetails && typeof data.errorDetails === 'object'
    ? (data.errorDetails as Partial<BillingSeatLimitDetails>)
    : {};
};

export const isSeatLimitError = (error: unknown) =>
  getSeatLimitDetails(error) !== null;

/** Share of the free tier in use, 0 to 100. */
export const seatUsagePercent = (active: number, freeLimit: number) =>
  freeLimit > 0 ? Math.min(100, Math.round((active / freeLimit) * 100)) : 100;

/** `04/2028`, or null without both parts. */
export const cardExpiry = (method: BillingPaymentMethod): string | null =>
  method.expMonth && method.expYear
    ? `${String(method.expMonth).padStart(2, '0')}/${method.expYear}`
    : null;

export const cardBrandName = (brand: string | null): string | null => {
  if (!brand) return null;
  const key = brand.toLowerCase();
  return CARD_BRAND_NAMES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
};

/** The `DataTable` paginator's meta for one page of invoices. */
export const toInvoicePaginationMeta = ({
  page,
  limit,
  total,
}: BillingInvoicePage): PaginationMeta => {
  const lastPage = Math.max(1, Math.ceil(total / Math.max(1, limit)));
  return {
    page,
    limit,
    totalCount: total,
    lastPage,
    hasPreviousPage: page > 1,
    hasNextPage: page < lastPage,
  };
};

export const PLAN_STATE_TONE: Record<BillingPlanState, Tone> = {
  [BillingPlanState.Free]: Tone.SOFT_GRAY,
  [BillingPlanState.Trial]: Tone.ACCENT,
  [BillingPlanState.Paid]: Tone.SUCCESS,
  [BillingPlanState.PastDue]: Tone.ERROR,
  [BillingPlanState.Comped]: Tone.SOFT_BLUE,
};

export const INVOICE_STATUS_TONE: Record<BillingInvoiceStatus, Tone> = {
  [BillingInvoiceStatus.Open]: Tone.PRIMARY,
  [BillingInvoiceStatus.Paid]: Tone.SUCCESS,
  [BillingInvoiceStatus.PastDue]: Tone.ERROR,
  [BillingInvoiceStatus.Void]: Tone.SECONDARY,
};
